(() => {
  const tag = 'ab-pd2-sound-kits';
  if (customElements.get(tag)) return;

  customElements.define(tag, class extends HTMLElement {
    connectedCallback() {
      this.initFrame = requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      if (!this.isConnected || this.controller) return;
      this.cards = [...this.querySelectorAll('[data-sound-card]')];
      this.audio = this.querySelector('[data-sound-audio]');
      this.stage = this.querySelector('[data-sound-stage]');
      this.playButton = this.querySelector('[data-sound-play]');
      this.status = this.querySelector('[data-sound-status]');
      if (!this.cards.length || !this.audio || !this.stage || !this.playButton) return;

      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.index = Math.max(0, Math.min((Number(this.dataset.start) || 0), this.cards.length - 1));
      this.generation = 0;
      this.pending = false;
      this.continuous = false;
      this.suppressClickUntil = 0;
      this.setAttribute('data-ready', '');

      this.cards.forEach((card, index) => {
        card.querySelector('[data-sound-select]').addEventListener('click', () => {
          if (Date.now() >= this.suppressClickUntil) this.select(index);
        }, { signal });
      });
      this.playButton.addEventListener('click', () => {
        if (Date.now() >= this.suppressClickUntil) this.togglePlayback();
      }, { signal });
      this.querySelector('[data-sound-prev]').addEventListener('click', () => this.select(this.index - 1), { signal });
      this.querySelector('[data-sound-next]').addEventListener('click', () => this.select(this.index + 1), { signal });
      this.stage.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.select(this.index + (event.key === 'ArrowLeft' ? -1 : 1));
        this.cards[this.index].querySelector('button').focus({ preventScroll: true });
      }, { signal });
      this.stage.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button !== 0) return;
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY };
      }, { signal });
      window.addEventListener('pointerup', event => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        const dx = event.clientX - this.drag.x;
        const dy = event.clientY - this.drag.y;
        this.drag = null;
        if (Math.abs(dx) < 35 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
        this.suppressClickUntil = Date.now() + 400;
        this.select(this.index + (dx < 0 ? 1 : -1));
      }, { signal });
      window.addEventListener('pointercancel', () => { this.drag = null; }, { signal });
      window.addEventListener('resize', () => this.render(), { passive: true, signal });
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.stop(); }, { signal });
      window.addEventListener('pagehide', () => this.stop(), { signal });
      document.addEventListener('pd2:sound-play', event => {
        if (event.detail !== this) this.stop();
      }, { signal });
      this.addEventListener('shopify:block:select', event => {
        const index = this.cards.indexOf(event.target.closest('[data-sound-card]'));
        if (index >= 0) {
          this.stop();
          this.select(index);
        }
      }, { signal });
      this.audio.addEventListener('playing', () => {
        if (!this.continuous || document.hidden || !this.isStageVisible()) {
          this.stop();
          return;
        }
        this.pending = false;
        this.status.textContent = '';
        this.syncPlayback();
      }, { signal });
      this.audio.addEventListener('pause', () => {
        // Source switches also queue pause events. Only an actual external pause ends the session.
        if (this.audio.paused && !this.audio.ended && !this.pending && this.audio.getAttribute('src')) {
          this.stop();
        } else {
          this.syncPlayback();
        }
      }, { signal });
      // Do not advance cards or loop; the next user-selected kit can still start automatically.
      this.audio.addEventListener('ended', () => {
        if (this.audio.ended) this.stop({ keepSession: true });
      }, { signal });
      this.audio.addEventListener('error', () => {
        if (!this.audio.getAttribute('src')) return;
        this.stop();
        this.status.textContent = 'Unable to play this sample. Please try again.';
      }, { signal });
      this.viewportObserver = new IntersectionObserver(entries => {
        const entry = entries[entries.length - 1];
        if (!entry.isIntersecting || entry.intersectionRatio < 0.15) this.stop();
      }, { threshold: [0, 0.15] });
      this.viewportObserver.observe(this.stage);
      this.render();
    }

    isStageVisible() {
      const rect = this.stage.getBoundingClientRect();
      if (!rect.width || !rect.height) return false;
      const width = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
      const height = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
      return (width * height) / (rect.width * rect.height) >= 0.15;
    }

    select(index) {
      const next = Math.max(0, Math.min(index, this.cards.length - 1));
      if (next === this.index) return;
      const continuePlaying = this.continuous && !document.hidden && this.isStageVisible();
      this.stop({ keepSession: continuePlaying });
      this.index = next;
      this.render();
      if (continuePlaying) this.playCurrent();
    }

    stop({ keepSession = false } = {}) {
      this.generation++;
      this.pending = false;
      if (!keepSession) this.continuous = false;
      this.audio.pause();
      if (this.audio.hasAttribute('src')) {
        this.audio.removeAttribute('src');
        this.audio.load();
      }
      this.status.textContent = '';
      this.syncPlayback();
    }

    togglePlayback() {
      if (this.pending || !this.audio.paused) {
        this.stop();
        return;
      }
      if (!this.cards[this.index].dataset.audio || document.hidden || !this.isStageVisible()) return;
      this.continuous = true;
      this.playCurrent();
    }

    async playCurrent() {
      if (!this.continuous || document.hidden || !this.isStageVisible()) {
        this.stop();
        return;
      }
      const src = this.cards[this.index].dataset.audio;
      // A card without a sample stays silent; selecting a valid card can continue the session.
      if (!src) return;
      document.dispatchEvent(new CustomEvent('pd2:sound-play', { detail: this }));
      const generation = ++this.generation;
      this.audio.src = src;
      this.pending = true;
      this.status.textContent = 'Loading sample…';
      this.syncPlayback();
      try {
        // Keep this in the click/swipe event call stack and reuse the same audio element.
        await this.audio.play();
        if (generation !== this.generation) return;
        this.pending = false;
        this.syncPlayback();
      } catch (error) {
        if (generation !== this.generation) return;
        this.stop();
        this.status.textContent = error.name === 'NotAllowedError'
          ? 'Tap play to continue listening.'
          : 'Unable to play this sample. Please try again.';
      }
    }

    syncPlayback() {
      const playing = !this.audio.paused && !this.audio.ended;
      const active = playing || this.pending;
      this.toggleAttribute('data-playing', active);
      this.playButton.setAttribute('aria-pressed', String(active));
      this.playButton.setAttribute('aria-busy', String(this.pending));
      const title = this.cards[this.index].dataset.title;
      this.playButton.setAttribute('aria-label', `${active ? 'Stop' : 'Play'} ${title} sample`);
    }

    render() {
      const step = parseFloat(getComputedStyle(this.stage).getPropertyValue('--card-step')) || 65;
      this.cards.forEach((card, index) => {
        const offset = index - this.index;
        const distance = Math.abs(offset);
        card.style.setProperty('--offset', `${offset * step}px`);
        card.style.setProperty('--scale', String(Math.max(.4, 1 - distance * .13)));
        card.style.setProperty('--layer', String(10 - distance));
        card.style.setProperty('--opacity', String(Math.max(.35, 1 - distance * .12)));
        card.toggleAttribute('data-offscreen', distance > 4);
        const button = card.querySelector('button');
        button.tabIndex = distance > 4 ? -1 : 0;
        button.setAttribute('aria-pressed', String(index === this.index));
      });
      this.querySelector('[data-sound-prev]').disabled = this.index === 0;
      this.querySelector('[data-sound-next]').disabled = this.index === this.cards.length - 1;
      this.querySelector('[data-sound-position]').textContent = `${this.index + 1} / ${this.cards.length}`;
      this.playButton.disabled = !this.cards[this.index].dataset.audio;
      this.syncPlayback();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.initFrame);
      this.viewportObserver?.disconnect();
      if (this.controller) {
        this.stop();
        this.controller.abort();
        this.controller = null;
      }
      this.removeAttribute('data-ready');
    }
  });
})();
